import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Custom Location Dropdown Component
 * A fully controlled dropdown with search functionality
 */
const LocationSelect = ({
    options = [],
    value,
    onChange,
    onBlur,
    placeholder = "Select...",
    disabled = false,
    error = false,
    searchable = true,
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Use local deferred state for filtering to avoid lag on every keystroke
    const [deferredSearchTerm, setDeferredSearchTerm] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDeferredSearchTerm(searchTerm);
        }, 300); // 300ms debounce
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Filter options based on search term with extreme performance optimization
    const filteredOptions = React.useMemo(() => {
        const cleanTerm = (deferredSearchTerm || '').trim().toLowerCase();
        if (!cleanTerm) return options;

        // Optimization: if too many options, only show matches that START with the term if term is short
        const isShortTerm = cleanTerm.length < 2;

        const filtered = options.filter(option => {
            const name = (option?.name || '').toLowerCase();
            if (isShortTerm) return name.startsWith(cleanTerm);
            return name.includes(cleanTerm);
        });

        // Optimization: Only sort if the result set is reasonably small
        if (filtered.length > 500) {
            return filtered; // Skip heavy sort for massive lists during typing
        }

        return filtered.sort((a, b) => {
            const aName = (a?.name || '').toLowerCase();
            const bName = (b?.name || '').toLowerCase();

            const aStarts = aName.startsWith(cleanTerm);
            const bStarts = bName.startsWith(cleanTerm);

            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            return aName.localeCompare(bName);
        });
    }, [options, deferredSearchTerm]);

    // Limit the number of items rendered to prevent lag with large datasets
    const displayOptions = React.useMemo(() => filteredOptions.slice(0, 100), [filteredOptions]);

    // Close dropdown when clicking outside

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // If there's a search term and no current value, treat it as a custom entry
                if (searchTerm.trim() !== '' && !value) {
                    handleSelect({ id: searchTerm, name: searchTerm });
                }
                setIsOpen(false);
                setSearchTerm('');
                if (onBlur) onBlur();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onBlur, isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div ref={dropdownRef} className={`relative ${isOpen ? 'z-50' : ''} ${className}`}>
            {/* Selected Value Display / Trigger */}
            <div
                onClick={toggleDropdown}
                className={`
          w-full px-3 py-2 border rounded-lg cursor-pointer
          flex items-center justify-between
          transition-all duration-200
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
          ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}
          ${isOpen ? 'ring-2 ring-agrosaf-green border-transparent' : ''}
        `}
            >
                <span className={value ? 'text-gray-900' : 'text-gray-500'}>
                    {value?.name || placeholder}
                </span>
                <div className="flex items-center gap-2">
                    {value && !disabled && (
                        <X
                            className="h-4 w-4 text-gray-400 hover:text-gray-600"
                            onClick={handleClear}
                        />
                    )}
                    <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''
                            }`}
                    />
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    {/* Search Input */}
                    {searchable && (
                        <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="overflow-y-auto max-h-48">
                        {displayOptions.length > 0 ? (
                            <>
                                {displayOptions.map((option, index) => (
                                    <div
                                        key={`${option.id}-${index}`}
                                        onClick={() => handleSelect(option)}
                                        className={`
                        px-3 py-2 cursor-pointer transition-colors
                        ${value?.id === option.id
                                                ? 'bg-agrosaf-green text-white'
                                                : 'hover:bg-gray-100 text-gray-900'
                                            }
                      `}
                                    >
                                        {option.name}
                                    </div>
                                ))}
                                {filteredOptions.length > 100 && (
                                    <div className="px-3 py-1 text-[10px] text-gray-400 border-t border-gray-50 italic">
                                        Showing top 100 results, keep typing to refine...
                                    </div>
                                )}
                            </>
                        ) : searchTerm.trim() !== '' ? (
                            <div
                                onClick={() => handleSelect({ id: searchTerm, name: searchTerm })}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-agrosaf-green font-medium flex items-center gap-2"
                            >
                                <span className="text-gray-500 font-normal">Use</span> "{searchTerm}"
                            </div>
                        ) : (
                            <div className="px-3 py-2 text-gray-500 text-center">
                                No results found
                            </div>
                        )}
                        {searchTerm.trim() !== '' && !filteredOptions.some(o => o.name.toLowerCase() === searchTerm.toLowerCase()) && filteredOptions.length > 0 && (
                            <div
                                onClick={() => handleSelect({ id: searchTerm, name: searchTerm })}
                                className="px-3 py-2 cursor-pointer border-t border-gray-100 hover:bg-gray-100 text-agrosaf-green font-medium flex items-center gap-2"
                            >
                                <span className="text-gray-500 font-normal">Use custom:</span> "{searchTerm}"
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationSelect;
